import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import studentPortalService from '../../services/studentPortalService';
import { API_URL } from '../../utils/api';
import { VideoCamera, ClipboardText, CheckCircle, Circle } from '@phosphor-icons/react';

// Hosted (uploaded) videos are stored as a relative /uploads/... path served
// by the backend, not the frontend dev server — needs the backend origin
// prefixed. External links (video_source 'link') are already absolute URLs.
const resolveVideoUrl = (item) => (item.video_source === 'upload' && item.video_url ? `${API_URL}${item.video_url}` : item.video_url);

function StudentModulesSection({ onGoToAssignments }) {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingItemId, setTogglingItemId] = useState(null);

  const loadModules = useCallback(() => {
    setLoading(true);
    studentPortalService.listCourseModules()
      .then(data => setModules(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Failed to load course modules'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadModules(); }, [loadModules]);

  const toggleComplete = async (item) => {
    setTogglingItemId(item.id);
    try {
      if (item.completed) {
        await studentPortalService.uncompleteModuleItem(item.id);
      } else {
        await studentPortalService.completeModuleItem(item.id);
      }
      loadModules();
    } catch (error) {
      toast.error(error.message || 'Failed to update progress');
    } finally {
      setTogglingItemId(null);
    }
  };

  if (loading) {
    return <div className="loading">Loading course modules...</div>;
  }

  if (modules.length === 0) {
    return (
      <div className="empty-state-card">
        <p>No course modules available yet</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 16 }} data-testid="student-modules-list">
      {modules.map(module => {
        const items = module.items || [];
        const completedCount = items.filter(i => i.completed).length;
        return (
          <div key={module.id} className="form-card" style={{ padding: 16 }}>
            <h3 style={{ margin: 0 }}>{module.title}</h3>
            {module.description && <p style={{ margin: '4px 0', color: '#666', fontSize: 14 }}>{module.description}</p>}
            <p style={{ margin: '4px 0 12px', fontSize: 13, color: '#888' }}>{completedCount} / {items.length} completed</p>

            <ul style={{ paddingLeft: 0, listStyle: 'none', display: 'grid', gap: 6 }}>
              {items.map(item => (
                item.assignment_id ? (
                  <li key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: '#f7f7f7', borderRadius: 6, flexWrap: 'wrap', gap: 6 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <ClipboardText size={16} />
                      <span>{item.title}</span>
                      <span className="badge inactive" style={{ fontSize: 11 }}>Quiz</span>
                      {item.assignment?.due_date && (
                        <span style={{ fontSize: 12, color: '#888' }}>Due {new Date(item.assignment.due_date).toLocaleDateString()}</span>
                      )}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {item.score != null && (
                        <span style={{ fontSize: 13, fontWeight: 600 }}>{item.score}/{item.assignment?.points_possible ?? '?'}</span>
                      )}
                      {item.completed ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#16a34a', fontSize: 13 }}>
                          <CheckCircle size={18} weight="fill" /> Submitted
                        </span>
                      ) : (
                        <button className="btn btn-sm btn-primary" type="button" onClick={() => onGoToAssignments && onGoToAssignments(item.assignment_id)}>
                          Take Quiz
                        </button>
                      )}
                    </span>
                  </li>
                ) : (
                  <li key={item.id} style={{ padding: '8px 10px', background: '#f7f7f7', borderRadius: 6 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <VideoCamera size={16} />
                        {item.video_source === 'upload' ? (
                          item.title
                        ) : item.video_url ? (
                          <a href={item.video_url} target="_blank" rel="noopener noreferrer">{item.title}</a>
                        ) : (
                          item.title
                        )}
                      </span>
                      <button
                        className="btn-icon"
                        type="button"
                        onClick={() => toggleComplete(item)}
                        disabled={togglingItemId === item.id}
                        title={item.completed ? 'Mark incomplete' : 'Mark complete'}
                      >
                        {item.completed ? <CheckCircle size={20} weight="fill" color="#16a34a" /> : <Circle size={20} />}
                      </button>
                    </div>
                    {item.video_source === 'upload' && item.video_url && (
                      <video
                        controls
                        preload="metadata"
                        style={{ width: '100%', maxHeight: 320, marginTop: 8, borderRadius: 6, background: '#000' }}
                        src={resolveVideoUrl(item)}
                      >
                        Your browser doesn't support inline video playback.
                      </video>
                    )}
                  </li>
                )
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

export default StudentModulesSection;
